import React, { useEffect, useRef, useState } from "react";
import Form from "react-bootstrap/Form";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { getCloudinaryMediaBaseUrl } from "../../config/cloudinaryBase";
import ConfirmModal from "../../components/confirmModal/confirmModel";
import CustomButton from "../../components/customButton/customButton";
import ClubBrandingAdvancedFields from "../../components/clubBranding/ClubBrandingAdvancedFields";
import BrandingGalleryField from "../../components/clubBranding/BrandingGalleryField";
import ClubBillingFields from "../../components/clubBranding/ClubBillingFields";
import ClubTableFeeFields from "../../components/clubBranding/ClubTableFeeFields";
import "../../components/clubBranding/clubBrandingFields.css";
import {
  BRANDING_GALLERY_MAX,
  CLUB_EXTENDED_BRANDING_KEYS,
  CLUB_WEBSITE_PAGE_KEYS,
  EMPTY_EXTENDED_CLUB_BRANDING,
  normalizeClubPages,
  pagesToPayload,
  pickExtendedBrandingFromClub,
  sanitizeClubUpdateRequestBody,
} from "../../config/clubWebsitePages";
import { resolveCloudinaryImageSrc, uploadFile } from "../../helpers/helper.service";
import {
  normalizeBrandingGalleryForState,
  pickBrandingGalleryFromApiPayload,
  uploadNewGalleryFilesToCloudinary,
} from "../../helpers/brandingGallery";
import {
  EMPTY_CLUB_BILLING,
  buildClubBillingPutFields,
  pickClubBillingFromClub,
  validateClubBilling,
} from "../../helpers/clubBilling";
import {
  EMPTY_CLUB_TABLE_FEES,
  clubTableFeePutFields,
  pickClubTableFees,
  validateClubTableFees,
} from "../../helpers/tableFeeSettings";
import { showToast } from "../../store/features/toast/toastSlice";

const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const hexPattern = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const defaultEmpty = {
  name: "",
  slug: "",
  logo: "",
  primarycolor: "#000000",
  secondarycolor: "#ffffff",
  theme: "",
  isactive: true,
};

/**
 * @param {{ showModal: boolean, onClose: () => void, mode: 'add'|'edit', initialClub: object|null, onSave: (data: object) => void, saving: boolean }} props
 */
const ClubFormModal = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showModal, onClose, mode, initialClub, onSave, saving, detailLoading } =
    props;
  const logoFileRef = useRef(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [pagesForm, setPagesForm] = useState(() => normalizeClubPages());
  const [extraBranding, setExtraBranding] = useState(() => ({
    ...EMPTY_EXTENDED_CLUB_BRANDING,
  }));
  const [brandingGallery, setBrandingGallery] = useState([]);
  const [billing, setBilling] = useState(() => ({ ...EMPTY_CLUB_BILLING }));
  const [billingErrors, setBillingErrors] = useState({});
  const [tableFees, setTableFees] = useState(() => ({ ...EMPTY_CLUB_TABLE_FEES }));
  const [tableFeeErrors, setTableFeeErrors] = useState({});

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: defaultEmpty,
  });

  const primarycolor = watch("primarycolor");
  const secondarycolor = watch("secondarycolor");
  const logoValue = watch("logo");
  const cloudinaryBase = getCloudinaryMediaBaseUrl();

  useEffect(() => {
    if (!showModal && logoFileRef.current) {
      logoFileRef.current.value = "";
    }
  }, [showModal]);

  useEffect(() => {
    if (!showModal) return;
    if (detailLoading) return;
    if (mode === "edit" && !initialClub) return;
    if (mode === "edit" && initialClub) {
      reset({
        name: initialClub.name ?? "",
        slug: initialClub.slug ?? "",
        logo: initialClub.logo ?? "",
        primarycolor: initialClub.primarycolor || "#000000",
        secondarycolor: initialClub.secondarycolor || "#ffffff",
        theme: initialClub.theme ?? "",
        isactive: initialClub.isactive !== false,
      });
      setPagesForm(normalizeClubPages(initialClub.pages));
      setExtraBranding(pickExtendedBrandingFromClub(initialClub));
      setBrandingGallery(
        normalizeBrandingGalleryForState(
          pickBrandingGalleryFromApiPayload(initialClub)
        )
      );
      setBilling(pickClubBillingFromClub(initialClub));
      setBillingErrors({});
      setTableFees(pickClubTableFees(initialClub));
      setTableFeeErrors({});
    } else {
      reset({ ...defaultEmpty });
      setPagesForm(normalizeClubPages());
      setExtraBranding({ ...EMPTY_EXTENDED_CLUB_BRANDING });
      setBrandingGallery([]);
      setBilling({ ...EMPTY_CLUB_BILLING });
      setBillingErrors({});
      setTableFees({ ...EMPTY_CLUB_TABLE_FEES });
      setTableFeeErrors({});
    }
  }, [showModal, mode, initialClub, reset, detailLoading]);

  const submit = async (data) => {
    const nextTableFeeErrors = validateClubTableFees(
      tableFees,
      t("CLUB_TABLE_FEE_PERCENT_INVALID")
    );
    const nextBillingErrors = validateClubBilling(billing, t);
    if (Object.keys(nextTableFeeErrors).length > 0) {
      setTableFeeErrors(nextTableFeeErrors);
    }
    if (Object.keys(nextBillingErrors).length > 0) {
      setBillingErrors(nextBillingErrors);
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_BILLING_VALIDATION_ERROR"),
          type: "error",
        })
      );
      return;
    }
    if (Object.keys(nextTableFeeErrors).length > 0) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_TABLE_FEE_PERCENT_INVALID"),
          type: "error",
        })
      );
      return;
    }
    setTableFeeErrors({});
    setBillingErrors({});
    if (brandingGallery.length > BRANDING_GALLERY_MAX) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_BRAND_GALLERY_TOO_MANY"),
          type: "error",
        })
      );
      return;
    }
    let resolvedGallery;
    try {
      resolvedGallery = await uploadNewGalleryFilesToCloudinary(brandingGallery);
    } catch (err) {
      dispatch(
        showToast({
          show: false,
          message: err?.message || t("CLUB_BRAND_GALLERY_UPLOAD_FAILED"),
          type: "error",
        })
      );
      return;
    }
    const body = {
      name: data.name?.trim(),
      slug: data.slug?.trim().toLowerCase(),
      primarycolor: data.primarycolor?.trim(),
      secondarycolor: data.secondarycolor?.trim(),
      theme: data.theme?.trim() || undefined,
      isactive: !!data.isactive,
      pages: pagesToPayload(pagesForm),
    };
    const logoTrim = (data.logo || "").trim();
    if (logoTrim) body.logo = logoTrim;
    const extBaseline =
      mode === "edit" && initialClub
        ? pickExtendedBrandingFromClub(initialClub)
        : null;
    CLUB_EXTENDED_BRANDING_KEYS.forEach((k) => {
      const v = String(extraBranding[k] ?? "").trim();
      if (k === "instagramurl") {
        if (extBaseline) {
          const prev = String(extBaseline[k] ?? "").trim();
          if (v) body[k] = v;
          else if (prev) body[k] = "";
        } else if (v) body[k] = v;
        return;
      }
      if (v) body[k] = v;
    });
    body.brandinggallery = resolvedGallery;
    body.billingDetails = buildClubBillingPutFields(billing);
    Object.assign(body, clubTableFeePutFields(tableFees));
    onSave(sanitizeClubUpdateRequestBody(body));
  };

  const onBillingFieldBlur = (key) => {
    if (key !== "gstin" && key !== "pan") return;
    setBilling((prev) => {
      const raw = prev[key];
      if (raw == null || raw === "") return prev;
      const upper = String(raw).trim().toUpperCase();
      return upper === raw ? prev : { ...prev, [key]: upper };
    });
    setBillingErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const galleryNotifyError = (message) => {
    dispatch(
      showToast({
        show: false,
        message,
        type: "error",
      })
    );
  };

  const onBackgroundImageUpload = async (fieldKey, file) => {
    if (!file?.type?.startsWith("image/")) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_LOGO_INVALID_TYPE"),
          type: "error",
        })
      );
      return;
    }
    try {
      const url = await uploadFile(file);
      setExtraBranding((prev) => ({ ...prev, [fieldKey]: url }));
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_BRAND_BG_IMAGE_READY"),
          type: "success",
        })
      );
    } catch (err) {
      dispatch(
        showToast({
          show: false,
          message:
            (typeof err?.message === "string" && err.message) ||
            t("CLUB_LOGO_UPLOAD_FAILED"),
          type: "error",
        })
      );
    }
  };

  const onLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_LOGO_INVALID_TYPE"),
          type: "error",
        })
      );
      return;
    }
    setLogoUploading(true);
    try {
      const url = await uploadFile(file);
      setValue("logo", url, { shouldValidate: true, shouldDirty: true });
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_LOGO_READY"),
          type: "success",
        })
      );
    } catch (err) {
      dispatch(
        showToast({
          show: false,
          message:
            (typeof err?.message === "string" && err.message) ||
            t("CLUB_LOGO_UPLOAD_FAILED"),
          type: "error",
        })
      );
    } finally {
      setLogoUploading(false);
    }
  };

  const logoPreviewSrc =
    logoValue?.trim() &&
    (/^https?:\/\//i.test(logoValue.trim()) ||
      logoValue.trim().startsWith("data:"))
      ? logoValue.trim()
      : logoValue?.trim()
        ? resolveCloudinaryImageSrc(logoValue.trim(), cloudinaryBase, "")
        : "";
  return (
    <ConfirmModal
      className="Deactivate import-model"
      modalTitle={
        mode === "add" ? t("CLUB_CREATE_TITLE") : t("CLUB_EDIT_TITLE")
      }
      showModal={showModal}
      isModelIcon={false}
      modalContent={false}
      isFirstButton={false}
      isSecondButton={false}
      onProcced={() => {}}
      closeModelIcon={() => onClose()}
      isCloseBtn={true}
    >
      <form onSubmit={handleSubmit(submit)} className="form-horizontal">
        {detailLoading || (mode === "edit" && !initialClub) ? (
          <p className="text-muted py-4 mb-0">{t("LOADING")}</p>
        ) : (
        <div className="uploadWrapper createPageWrapper">
          <div className="d-flex">
            <p className="form-label">{t("CLUB_NAME")}</p>
            <p className="requiredStyle">*</p>
          </div>
          <input
            className={`form-control ${errors.name ? "is-invalid" : ""}`}
            {...register("name", { required: t("CLUB_NAME_REQUIRED") })}
          />
          {errors.name && (
            <div className="invalid-feedback d-block">{errors.name.message}</div>
          )}

          <div className="d-flex mt-3">
            <p className="form-label">{t("CLUB_SLUG")}</p>
            <p className="requiredStyle">*</p>
          </div>
          <input
            className={`form-control ${errors.slug ? "is-invalid" : ""}`}
            {...register("slug", {
              required: t("CLUB_SLUG_REQUIRED"),
              pattern: {
                value: slugPattern,
                message: t("CLUB_SLUG_INVALID"),
              },
            })}
            onBlur={(e) =>
              setValue("slug", e.target.value.trim().toLowerCase(), {
                shouldValidate: true,
              })
            }
          />
          {errors.slug && (
            <div className="invalid-feedback d-block">{errors.slug.message}</div>
          )}

          <div className="d-flex mt-3">
            <p className="form-label">{t("CLUB_LOGO")}</p>
          </div>
          <p className="text-muted small mb-2">{t("CLUB_LOGO_UPLOAD_HINT")}</p>
          <input type="hidden" {...register("logo")} />
          <div className="club-brand-media-row mb-2">
            <div className="club-brand-thumb-wrap">
              {logoPreviewSrc ? (
                <img
                  src={logoPreviewSrc}
                  alt=""
                  className="club-brand-thumb"
                  onError={(ev) => {
                    ev.target.style.display = "none";
                  }}
                />
              ) : (
                <span className="club-brand-thumb-placeholder">
                  {t("CLUB_BRAND_IMG_PREVIEW_EMPTY")}
                </span>
              )}
            </div>
            <div className="club-brand-media-actions">
              <input
                ref={logoFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                className="form-control club-brand-file-input"
                disabled={saving || logoUploading}
                onChange={onLogoFileChange}
              />
              {logoUploading ? (
                <span className="text-muted small">{t("CLUB_LOGO_UPLOADING")}</span>
              ) : null}
              {logoValue?.trim() ? (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm align-self-start"
                  disabled={saving || logoUploading}
                  onClick={() => {
                    setValue("logo", "", { shouldDirty: true });
                    if (logoFileRef.current) {
                      logoFileRef.current.value = "";
                    }
                  }}
                >
                  {t("CLUB_BRAND_BG_CLEAR")}
                </button>
              ) : null}
            </div>
          </div>

          <div className="d-flex mt-3">
            <p className="form-label">{t("CLUB_PRIMARY_COLOR")}</p>
            <p className="requiredStyle">*</p>
          </div>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <input
              type="color"
              className="form-control form-control-color"
              title={t("CLUB_PRIMARY_COLOR")}
              value={primarycolor?.match(hexPattern) ? primarycolor : "#000000"}
              onChange={(e) =>
                setValue("primarycolor", e.target.value, { shouldValidate: true })
              }
            />
            <input
              className={`form-control ${errors.primarycolor ? "is-invalid" : ""}`}
              {...register("primarycolor", {
                required: t("CLUB_COLOR_REQUIRED"),
                pattern: {
                  value: hexPattern,
                  message: t("CLUB_COLOR_INVALID"),
                },
              })}
            />
          </div>
          {errors.primarycolor && (
            <div className="invalid-feedback d-block">
              {errors.primarycolor.message}
            </div>
          )}

          <div className="d-flex mt-3">
            <p className="form-label">{t("CLUB_SECONDARY_COLOR")}</p>
            <p className="requiredStyle">*</p>
          </div>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <input
              type="color"
              className="form-control form-control-color"
              title={t("CLUB_SECONDARY_COLOR")}
              value={
                secondarycolor?.match(hexPattern) ? secondarycolor : "#ffffff"
              }
              onChange={(e) =>
                setValue("secondarycolor", e.target.value, {
                  shouldValidate: true,
                })
              }
            />
            <input
              className={`form-control ${errors.secondarycolor ? "is-invalid" : ""}`}
              {...register("secondarycolor", {
                required: t("CLUB_COLOR_REQUIRED"),
                pattern: {
                  value: hexPattern,
                  message: t("CLUB_COLOR_INVALID"),
                },
              })}
            />
          </div>
          {errors.secondarycolor && (
            <div className="invalid-feedback d-block">
              {errors.secondarycolor.message}
            </div>
          )}

          <div className="d-flex mt-3">
            <p className="form-label">{t("CLUB_THEME")}</p>
          </div>
          <input className="form-control" {...register("theme")} />

          <div className="mt-3 border rounded p-3">
            <ClubTableFeeFields
              t={t}
              values={tableFees}
              errors={tableFeeErrors}
              disabled={saving || detailLoading}
              onFieldChange={(key, value) => {
                setTableFees((prev) => ({ ...prev, [key]: value }));
                if (tableFeeErrors[key]) {
                  setTableFeeErrors((prev) => {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                  });
                }
              }}
            />
          </div>

          <details className="mt-3 border rounded p-3">
            <summary className="fw-semibold">{t("CLUB_BRAND_ADVANCED")}</summary>
            <p className="text-muted small mt-2">{t("CLUB_BRAND_ADVANCED_ADMIN_HELP")}</p>
            <ClubBrandingAdvancedFields
              t={t}
              values={extraBranding}
              primaryFallback={primarycolor}
              uploadsDisabled={saving}
              onBackgroundImageUpload={onBackgroundImageUpload}
              onFieldChange={(key, value) =>
                setExtraBranding((prev) => ({ ...prev, [key]: value }))
              }
            />
          </details>

          <div className="mt-3 border rounded p-3">
            <BrandingGalleryField
              t={t}
              images={brandingGallery}
              onChange={setBrandingGallery}
              disabled={saving}
              onError={galleryNotifyError}
            />
          </div>

          <div className="mt-3 border rounded p-3">
            <ClubBillingFields
              t={t}
              clubName={watch("name") || initialClub?.name || ""}
              values={billing}
              errors={billingErrors}
              disabled={saving || detailLoading}
              onFieldChange={(key, value) => {
                setBilling((prev) => ({ ...prev, [key]: value }));
                if (billingErrors[key]) {
                  setBillingErrors((prev) => {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                  });
                }
              }}
              onFieldBlur={onBillingFieldBlur}
            />
          </div>

          <Controller
            name="isactive"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Form.Check
                type="switch"
                id="club-isactive"
                className="mt-3"
                label={t("CLUB_ACTIVE")}
                checked={!!value}
                onChange={(e) => onChange(e.target.checked)}
              />
            )}
          />
          <hr className="my-4" />
          <p className="form-label fw-semibold">{t("CLUB_MINI_SITE_PAGES")}</p>
          {CLUB_WEBSITE_PAGE_KEYS.map(({ key, labelKey }) => (
            <div key={key} className="mt-3 border rounded p-3">
              <p className="form-label mb-2">{t(labelKey)}</p>
              <input
                className="form-control mb-2"
                placeholder={t("CLUB_PAGE_TITLE_PH")}
                value={pagesForm[key]?.title ?? ""}
                onChange={(e) =>
                  setPagesForm((prev) => ({
                    ...prev,
                    [key]: { ...prev[key], title: e.target.value },
                  }))
                }
              />
              <textarea
                className="form-control"
                rows={4}
                placeholder={t("CLUB_PAGE_BODY_PH")}
                value={pagesForm[key]?.body ?? ""}
                onChange={(e) =>
                  setPagesForm((prev) => ({
                    ...prev,
                    [key]: { ...prev[key], body: e.target.value },
                  }))
                }
              />
            </div>
          ))}
        </div>
        )}
        <div className="Form_buton">
          <CustomButton
            className="black-btn fillButton"
            type="submit"
            loading={saving}
            disabled={saving || detailLoading || (mode === "edit" && !initialClub)}
          >
            {mode === "add" ? t("CLUB_CREATE_BTN") : t("UPDATE")}
          </CustomButton>
          <CustomButton
            className="black-btn borderButton"
            type="button"
            onClick={() => onClose()}
          >
            {t("CANCEL")}
          </CustomButton>
        </div>
      </form>
    </ConfirmModal>
  );
};

export default ClubFormModal;
